import React, { useEffect } from 'react';

const AbstractFactoryPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Abstract Factory Pattern - Creational Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Abstract Factory Pattern - create families of related objects without specifying their concrete classes. Learn with GUI components and cross-platform examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Abstract Factory Pattern - create families of related objects without specifying their concrete classes. Learn with GUI components and cross-platform examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'abstract factory pattern, design patterns, creational patterns, object families, GUI components, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'abstract factory pattern, design patterns, creational patterns, object families, GUI components, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Abstract Factory Pattern</h1>
        <p style={styles.subtitle}>
          Creational Design Pattern - Create families of related objects
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Abstract Factory pattern provides an interface for creating families of related or 
            dependent objects without specifying their concrete classes. It's like a factory of factories.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            Imagine you need to create different families of products that work together. For example, 
            a furniture store that sells chairs, tables, and sofas in different styles (Modern, Victorian, ArtDeco). 
            You need to ensure that products from the same family are compatible with each other.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: GUI Components</h2>
          <div style={styles.codeBlock}>
{`// Abstract products
interface Button {
    void render();
    void onClick();
}

interface Checkbox {
    void render();
    void toggle();
}

interface TextField {
    void render();
    void setText(String text);
    String getText();
}

// Windows family
class WindowsButton implements Button {
    public void render() { System.out.println("Rendering Windows button"); }
    public void onClick() { System.out.println("Windows button clicked"); }
}

class WindowsCheckbox implements Checkbox {
    public void render() { System.out.println("Rendering Windows checkbox"); }
    public void toggle() { System.out.println("Windows checkbox toggled"); }
}

class WindowsTextField implements TextField {
    private String text = "";
    public void render() { System.out.println("Rendering Windows text field"); }
    public void setText(String text) { this.text = text; }
    public String getText() { return text; }
}

// Mac family
class MacButton implements Button {
    public void render() { System.out.println("Rendering Mac button"); }
    public void onClick() { System.out.println("Mac button clicked"); }
}

class MacCheckbox implements Checkbox {
    public void render() { System.out.println("Rendering Mac checkbox"); }
    public void toggle() { System.out.println("Mac checkbox toggled"); }
}

class MacTextField implements TextField {
    private String text = "";
    public void render() { System.out.println("Rendering Mac text field"); }
    public void setText(String text) { this.text = text; }
    public String getText() { return text; }
}

// Abstract factory
interface GUIFactory {
    Button createButton();
    Checkbox createCheckbox();
    TextField createTextField();
}

// Concrete factories
class WindowsFactory implements GUIFactory {
    public Button createButton() { return new WindowsButton(); }
    public Checkbox createCheckbox() { return new WindowsCheckbox(); }
    public TextField createTextField() { return new WindowsTextField(); }
}

class MacFactory implements GUIFactory {
    public Button createButton() { return new MacButton(); }
    public Checkbox createCheckbox() { return new MacCheckbox(); }
    public TextField createTextField() { return new MacTextField(); }
}

// Client code
class Application {
    private Button button;
    private Checkbox checkbox;
    private TextField textField;
    
    public Application(GUIFactory factory) {
        button = factory.createButton();
        checkbox = factory.createCheckbox();
        textField = factory.createTextField();
    }
    
    public void render() {
        button.render();
        checkbox.render();
        textField.render();
    }
}

// Usage
public class AbstractFactoryDemo {
    public static void main(String[] args) {
        GUIFactory factory;
        
        if (System.getProperty("os.name").contains("Windows")) {
            factory = new WindowsFactory();
        } else {
            factory = new MacFactory();
        }
        
        Application app = new Application(factory);
        app.render();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Consistency:</strong> Ensures products from same family work together</li>
            <li style={styles.listItem}><strong>Isolation:</strong> Isolates concrete classes from client code</li>
            <li style={styles.listItem}><strong>Easy Switching:</strong> Easy to change entire product families</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Each factory handles one family</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to create families of related products</li>
            <li style={styles.listItem}>When you want to ensure product compatibility</li>
            <li style={styles.listItem}>When you need to support multiple product lines</li>
            <li style={styles.listItem}>When you want to hide product creation complexity</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Abstract Factory pattern is powerful for creating families of related objects while 
            maintaining consistency and flexibility. It's particularly useful in cross-platform 
            applications where you need different implementations for different environments.
          </p>
        </section>
      </main>
    </article>
  );
};

export default AbstractFactoryPattern;